import { Button } from '@/components/ui/button';
import { router } from '@inertiajs/react';
import { useEffect, useRef, useState } from 'react';

import { MobileSearchOverlay } from '@/features/search-bar/components/mobile-search-overlay';
import { SearchDatePicker } from '@/features/search-bar/components/search-date-picker';
import { SearchGuestsPicker } from '@/features/search-bar/components/search-guests-picker';
import { SuggestionsDropdown } from '@/features/search-bar/components/suggestions-dropdown';
import { useCalendarPopover } from '@/features/search-bar/hooks/use-calendar-popover';
import { useSearchHistory } from '@/features/search-bar/hooks/use-search-history';
import { useSearchSuggestions } from '@/features/search-bar/hooks/use-search-suggestions';
import type {
    HistoryItem,
    SearchBarProps,
    Suggestion,
} from '@/features/search-bar/types';
import { toDateParam } from '@/features/search-bar/utils';
import { useDateContext } from '@/hooks/use-date-context';
import { useSearchContext } from '@/hooks/use-search-context';
import Icon from '@/icons/icon';

export default function SearchBar({
    initialQuery,
    initialAdults,
    initialChildren,
    initialRooms,
    onSearch,
}: SearchBarProps) {
    const {
        query,
        setQuery,
        adults,
        setAdults,
        children,
        setChildren,
        rooms,
        setRooms,
    } = useSearchContext();

    const {
        suggestions,
        setSuggestions,
        loadingSuggestions,
        setSelectedState,
        fetchSuggestions,
    } = useSearchSuggestions();
    const history = useSearchHistory();
    const { searchHistory, saveToHistory } = history;
    const {
        openCalendar,
        setOpenCalendar,
        calendarRef,
        mobileCalendarRef,
        checkInRef,
        calendarPos,
    } = useCalendarPopover();

    const [showSuggestions, setShowSuggestions] = useState(false);
    const suggestionRef = useRef<HTMLUListElement | null>(null);
    const [showError, setShowError] = useState(false);
    const [showCheckoutError, setShowCheckoutError] = useState(false);
    const [mobileOverlayOpen, setMobileOverlayOpen] = useState(false);
    const mobileInputRef = useRef<HTMLInputElement | null>(null);

    const [guestsOpen, setGuestsOpen] = useState(false);
    const guestsRef = useRef<HTMLDivElement | null>(null);

    // Use global date context
    const { startDate, endDate, setDates } = useDateContext();

    // Sync props to context
    useEffect(() => {
        if (initialQuery !== undefined) setQuery(initialQuery);
        if (initialAdults !== undefined) setAdults(initialAdults);
        if (initialChildren !== undefined) setChildren(initialChildren);
        if (initialRooms !== undefined) setRooms(initialRooms);
    }, [
        initialQuery,
        initialAdults,
        initialChildren,
        initialRooms,
        setQuery,
        setAdults,
        setChildren,
        setRooms,
    ]);

    const handleSearch = (): void => {
        if (!query.trim()) {
            setShowError(true);
            return;
        }
        setShowError(false);

        if (startDate && !endDate) {
            setShowCheckoutError(true);
            setOpenCalendar(true);
            return;
        }
        setShowCheckoutError(false);

        const params: Record<string, string> = {
            query: query.trim(),
        };

        if (startDate) {
            params.startDate = toDateParam(startDate);
        }
        if (endDate) {
            params.endDate = toDateParam(endDate);
        }
        if (adults > 0) {
            params.adults = String(adults);
        }
        if (children > 0) {
            params.children = String(children);
        }
        if (rooms > 0) {
            params.rooms = String(rooms);
        }

        saveToHistory({
            query: query.trim(),
            startDate: startDate ? toDateParam(startDate) : undefined,
            endDate: endDate ? toDateParam(endDate) : undefined,
            adults,
            children,
            rooms,
        });

        router.visit('/motels', {
            method: 'get',
            data: params,
            preserveState: false,
            preserveScroll: false,
        });

        setShowSuggestions(false);

        if (onSearch) {
            onSearch(query);
        }
    };

    useEffect(() => {
        function handleClick(event: PointerEvent): void {
            const target = event.target as Node;

            const insideDesktopCal =
                calendarRef.current?.contains(target) ?? false;
            const insideMobileCal =
                mobileCalendarRef.current?.contains(target) ?? false;
            if (!insideDesktopCal && !insideMobileCal) {
                setOpenCalendar(false);
                setShowCheckoutError(false);
            }
            if (guestsRef.current && !guestsRef.current.contains(target)) {
                setGuestsOpen(false);
            }
            if (
                suggestionRef.current &&
                !suggestionRef.current.contains(target)
            ) {
                setShowSuggestions(false);
            }
            setShowError(false);
        }

        document.addEventListener('pointerdown', handleClick);
        return () => document.removeEventListener('pointerdown', handleClick);
    }, [calendarRef, mobileCalendarRef, setOpenCalendar]);

    const handleSuggestionClick = (suggestion: Suggestion): void => {
        setQuery(suggestion.name);

        // If it's a state, update selectedState and refetch suggestions
        if (suggestion.type === 'state') {
            setSelectedState(suggestion.name);
        }

        setShowSuggestions(false);
        setMobileOverlayOpen(false);
    };

    const handleSelectHistory = (item: HistoryItem): void => {
        setQuery(item.query);
        setShowSuggestions(false);
    };

    const handleSelectMobileHistory = (item: HistoryItem): void => {
        setQuery(item.query);
        setMobileOverlayOpen(false);
    };

    const handleMobileQueryChange = (value: string): void => {
        setQuery(value);
        fetchSuggestions(value);
    };

    const handleMobileSubmit = (): void => {
        setMobileOverlayOpen(false);
        handleSearch();
    };

    const handleMobileClear = (): void => {
        if (query.trim() === '') {
            setMobileOverlayOpen(false);
            setShowSuggestions(false);
        } else {
            setQuery('');
            setSuggestions([]);
            mobileInputRef.current?.focus();
        }
    };

    return (
        <div className="flex w-full justify-center px-4">
            <div className="relative flex w-full max-w-4xl flex-wrap items-center overflow-visible rounded-xl bg-card shadow-2xl">
                {/* LOCATION INPUT */}
                <div className="relative min-w-30 flex-1">
                    <Icon
                        iconName="Location"
                        className="absolute top-1/2 left-3 h-5 w-5 shrink-0 -translate-y-1/2 fill-gray-500"
                    />

                    <input
                        className="w-full flex-1 bg-transparent px-10 py-4 font-semibold text-black outline-none dark:text-foreground"
                        placeholder="Where are you going?"
                        value={query}
                        onFocus={(e) => {
                            if (window.innerWidth < 640) {
                                e.currentTarget.blur();
                                setMobileOverlayOpen(true);
                                return;
                            }
                            if (
                                suggestions.length > 0 ||
                                searchHistory.length > 0
                            ) {
                                setShowSuggestions(true);
                            }
                        }}
                        onChange={(e) => {
                            setQuery(e.target.value);
                            setShowError(false);
                            setShowSuggestions(true);
                            fetchSuggestions(e.target.value);
                        }}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                                handleSearch();
                            }
                        }}
                    />

                    {showError && (
                        <div className="absolute top-[calc(100%+4px)] left-0 z-60 animate-in duration-200 fade-in slide-in-from-top-1">
                            <div className="relative rounded-md bg-red-600 px-4 py-2 text-sm font-medium whitespace-nowrap text-white shadow-lg">
                                {/* Arrow */}
                                <div className="absolute -top-1 left-6 h-2 w-2 rotate-45 bg-red-600" />
                                Enter a destination to start searching.
                            </div>
                        </div>
                    )}
                </div>

                {/* MOBILE FULL-SCREEN SEARCH OVERLAY */}
                {mobileOverlayOpen && (
                    <MobileSearchOverlay
                        inputRef={mobileInputRef}
                        query={query}
                        suggestions={suggestions}
                        loadingSuggestions={loadingSuggestions}
                        history={history}
                        onQueryChange={handleMobileQueryChange}
                        onSubmit={handleMobileSubmit}
                        onClear={handleMobileClear}
                        onSelectSuggestion={handleSuggestionClick}
                        onSelectHistory={handleSelectMobileHistory}
                    />
                )}

                {/* SUGGESTIONS DROPDOWN (desktop only) */}
                {showSuggestions && (
                    <SuggestionsDropdown
                        listRef={suggestionRef}
                        query={query}
                        suggestions={suggestions}
                        loadingSuggestions={loadingSuggestions}
                        history={history}
                        onSelectSuggestion={handleSuggestionClick}
                        onSelectHistory={handleSelectHistory}
                    />
                )}

                {/* CHECK-IN / CHECK-OUT */}
                <SearchDatePicker
                    startDate={startDate}
                    endDate={endDate}
                    setDates={setDates}
                    openCalendar={openCalendar}
                    setOpenCalendar={setOpenCalendar}
                    showCheckoutError={showCheckoutError}
                    setShowCheckoutError={setShowCheckoutError}
                    calendarPos={calendarPos}
                    checkInRef={checkInRef}
                    calendarRef={calendarRef}
                    mobileCalendarRef={mobileCalendarRef}
                />

                {/* GUESTS */}
                <SearchGuestsPicker
                    adults={adults}
                    setAdults={setAdults}
                    childrenCount={children}
                    setChildrenCount={setChildren}
                    rooms={rooms}
                    setRooms={setRooms}
                    guestsOpen={guestsOpen}
                    setGuestsOpen={setGuestsOpen}
                    guestsRef={guestsRef}
                />

                {/* SEARCH BUTTON */}
                <Button
                    variant="bare"
                    onClick={handleSearch}
                    className="w-full shrink-0 cursor-pointer rounded-b-xl bg-blue-600 px-6 py-4 font-semibold text-white transition-colors hover:bg-blue-700 lg:w-auto lg:rounded-xl"
                >
                    Search
                </Button>
            </div>
        </div>
    );
}
